/**
 * NiV Support API - Admin operations for support tickets
 * 
 * Uses Firebase Admin SDK (bypasses security rules) for admin operations.
 */

import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  getAllSupportTickets,
  addAdminMessage,
  updateTicketStatus,
  deleteSupportTicket,
} from "../../services/nivSupportFirebase";

export const nivSupportApi = createApi({
  reducerPath: "nivSupportApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["NivSupportTickets", "NivSupportTicket"],
  endpoints: (builder) => ({
    // ========================================================================
    // QUERIES
    // ========================================================================

    /**
     * Get all support tickets (admin view)
     */
    getAllSupportTickets: builder.query({
      async queryFn() {
        try {
          const tickets = await getAllSupportTickets();
          return { data: tickets };
        } catch (error) {
          console.error("[CK-7970] Error in getAllSupportTickets:", {
            error: error.message,
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "NivSupportTickets", id })),
              { type: "NivSupportTickets", id: "LIST" },
            ]
          : [{ type: "NivSupportTickets", id: "LIST" }],
    }),

    // ========================================================================
    // MUTATIONS
    // ========================================================================

    /**
     * Add an admin message to a ticket
     */
    addAdminMessage: builder.mutation({
      async queryFn({ ticketId, content, clinicId, therapistId }) {
        try {
          const ticket = await addAdminMessage(ticketId, content, clinicId, therapistId);
          return { data: ticket };
        } catch (error) {
          console.error("[CK-7971] Error in addAdminMessage:", {
            error: error.message,
            ticketId,
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { ticketId }) => [
        { type: "NivSupportTicket", id: ticketId },
        { type: "NivSupportTickets", id: "LIST" },
      ],
    }),

    /**
     * Update ticket status (admin only)
     */
    updateTicketStatus: builder.mutation({
      async queryFn({ ticketId, status, clinicId, therapistId, shouldNotify }) {
        try {
          const ticket = await updateTicketStatus(ticketId, status, clinicId, therapistId, shouldNotify);
          return { data: ticket };
        } catch (error) {
          console.error("[CK-7972] Error in updateTicketStatus:", {
            error: error.message,
            ticketId,
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { ticketId }) => [
        { type: "NivSupportTicket", id: ticketId },
        { type: "NivSupportTickets", id: "LIST" },
      ],
    }),

    /**
     * Delete a support ticket (admin only)
     */
    deleteTicket: builder.mutation({
      async queryFn({ ticketId }) {
        try {
          await deleteSupportTicket(ticketId);
          return { data: { success: true } };
        } catch (error) {
          console.error("[CK-7973] Error in deleteTicket:", {
            error: error.message,
            ticketId,
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "NivSupportTickets", id: "LIST" }],
    }),
  }),
});

// Export hooks
export const {
  useGetAllSupportTicketsQuery,
  useAddAdminMessageMutation,
  useUpdateTicketStatusMutation,
  useDeleteTicketMutation,
} = nivSupportApi;
